import React, { useEffect, useRef } from 'react';
import { useVideoConfig, useCurrentFrame } from 'remotion';
import { EffectType } from '../effects';
import { EffectShape, EffectResult } from '../effects/BaseEffect';
import { ObjectFit } from '../types/style';
import { getObjectFitDrawRect } from '../Utils';
import { getCloudFrontUrl } from '../../helper/CloudFrontUrlHelper';

interface CanvasRendererForImageProps {
    src: string;
    effects: EffectType[];
    style?: {
        objectFit?: ObjectFit;
    };
}

export const CanvasRendererForImage: React.FC<CanvasRendererForImageProps> = ({
    src,
    effects,
    style
}) => {
    const { fps } = useVideoConfig();
    const currentFrame = useCurrentFrame();
    const imgRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const img = imgRef.current;
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!img || !canvas || !container) return;

        const displayWidth = container.clientWidth;
        const displayHeight = container.clientHeight;

        if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
            canvas.width = displayWidth;
            canvas.height = displayHeight;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const renderFrame = () => {
            if (!img.naturalWidth || !img.naturalHeight) return;
            ctx.clearRect(0, 0, displayWidth, displayHeight);
            ctx.save();

            // Calculate draw rect for object-fit
            const { drawX, drawY, drawWidth, drawHeight } = getObjectFitDrawRect(
                style?.objectFit,
                img.naturalWidth,
                img.naturalHeight,
                displayWidth,
                displayHeight
            );
            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

            // Apply effects in sequence (using display size), passing the initial rect
            if (effects) {
                let effectResult: EffectResult = { shape: 'rect', rect: { x: drawX, y: drawY, width: drawWidth, height: drawHeight } };
                effects.forEach(effect => {
                    const currentTime = currentFrame / fps;
                    const progress = effect.calculateProgress(currentTime);
                    effectResult = effect.apply(ctx, {
                        resolutionWidth: displayWidth,
                        resolutionHeight: displayHeight,
                        elementWidth: canvas.clientWidth,
                        elementHeight: canvas.clientHeight
                    }, progress, effectResult) || effectResult;
                });
            }
            ctx.restore();
        };

        if (img.complete && img.naturalWidth && img.naturalHeight) {
            renderFrame();
        } else {
            img.onload = renderFrame;
        }

        // Redraw on resize
        const resizeObserver = new ResizeObserver(() => {
            if (canvas && container) {
                const newWidth = container.clientWidth;
                const newHeight = container.clientHeight;
                if (canvas.width !== newWidth || canvas.height !== newHeight) {
                    canvas.width = newWidth;
                    canvas.height = newHeight;
                }
                renderFrame();
            }
        });
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [currentFrame, effects, fps, style]);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                height: '100%',
                position: 'relative'
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                }}
            />
            <img
                ref={imgRef}
                src={getCloudFrontUrl(src)}
                style={{ display: 'none' }}
                alt=""
            />
        </div>
    );
};
